import React, { useState, useRef, useEffect } from 'react';
import {
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import {
  PhotoCamera,
  Delete,
  CloudUpload
} from '@mui/icons-material';
import ProfilePictureService from '../services/profilePictureService';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

const ProfilePictureUpload = ({ 
  size = 120, 
  showUploadButton = true, 
  sx = {},
  onUploadSuccess,
  onUploadError 
}) => {
  const { currentUser } = useAuth();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [currentPhotoURL, setCurrentPhotoURL] = useState(null);
  const fileInputRef = useRef(null);

  // Load user profile picture on mount and when user changes
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (currentUser) {
        const photoURL = await ProfilePictureService.getUserProfilePictureAsync(currentUser, size);
        setCurrentPhotoURL(photoURL);
      } else {
        setCurrentPhotoURL(ProfilePictureService.getUserProfilePicture(null, size));
      }
    };
    
    loadProfilePicture();
  }, [currentUser, size]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFile || !currentUser) return;

    try {
      setUploading(true);
      setError('');

      // For now, skip compression to isolate the issue
      const compressedFile = selectedFile;
      
      // Delete old profile picture if it exists and isn't a generated avatar
      if (currentUser.photoURL && !currentUser.photoURL.includes('ui-avatars.com')) {
        await ProfilePictureService.deleteProfilePicture(currentUser.photoURL);
      }

      // Upload new profile picture
      const result = await ProfilePictureService.uploadProfilePicture(
        compressedFile, 
        currentUser.uid
      );

      if (result.success) {
        // Refresh the profile picture
        setCurrentPhotoURL(result.url);
        
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setPreviewUrl(null);
        
        // Call success callback if provided
        if (onUploadSuccess) {
          onUploadSuccess(result.url);
        }
      }

    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload profile picture');
      
      // Call error callback if provided
      if (onUploadError) {
        onUploadError(error);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDialogClose = () => {
    if (uploading) return; // Prevent closing during upload
    
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setError('');
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    if (!currentUser?.photoURL || currentUser.photoURL.includes('ui-avatars.com')) return;

    try {
      setUploading(true);
      
      // Delete from storage
      await ProfilePictureService.deleteProfilePicture(currentUser.photoURL);
      
      // Update user profile to remove photo URL
      await updateProfile(currentUser, { photoURL: null });
      
      // Update Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        photoURL: null,
        profilePictureUpdatedAt: new Date()
      });

      if (onUploadSuccess) {
        onUploadSuccess(null);
      }

    } catch (error) {
      console.error('Error removing profile picture:', error);
      setError('Failed to remove profile picture');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-block', ...sx }}>
        <Avatar
          src={currentPhotoURL}
          sx={{
            width: size,
            height: size,
            fontSize: size / 3,
            border: '3px solid',
            borderColor: 'primary.main'
          }}
        >
          {!currentPhotoURL && (currentUser?.displayName?.charAt(0) || 'U')}
        </Avatar>
        
        {showUploadButton && (
          <Fade in={!uploading}>
            <IconButton
              sx={{
                position: 'absolute',
                bottom: -8,
                right: -8,
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                width: 40,
                height: 40
              }}
              onClick={() => setUploadDialogOpen(true)}
              disabled={uploading}
            >
              <PhotoCamera fontSize="small" />
            </IconButton>
          </Fade>
        )}

        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.5)',
              borderRadius: '50%'
            }}
          >
            <CircularProgress size={size / 4} sx={{ color: 'white' }} />
          </Box>
        )}
      </Box>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Profile Picture</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              src={previewUrl || currentPhotoURL}
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                fontSize: 40
              }}
            >
              {!previewUrl && !currentPhotoURL && (currentUser?.displayName?.charAt(0) || 'U')}
            </Avatar>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              sx={{ mr: 1 }}
            >
              Choose Photo
            </Button>

            {currentUser?.photoURL && !currentUser.photoURL.includes('ui-avatars.com') && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={handleRemovePhoto}
                disabled={uploading}
              >
                Remove
              </Button>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Supported formats: JPG, PNG, GIF (max 5MB)
            <br />
            Square images work best for profile pictures
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfilePictureUpload;